import React, { useState } from 'react';
import api from './services/api';
import { Search, X, Copy, AlertTriangle, ArrowRight, CornerDownRight } from 'lucide-react';
import { Toast } from './Toast';

interface IDFinderModalProps {
    onClose: () => void;
    accountId: number | string | null;
}

export const IDFinderModal: React.FC<IDFinderModalProps & { onSelectId?: (id: string, target: 'from' | 'to', title?: string) => void }> = ({ onClose, accountId, onSelectId }) => {
    const [query, setQuery] = useState('');
    const [result, setResult] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'info' | 'error' } | null>(null);

    const handleLookup = async () => {
        if (!accountId) {
            setError("Nenhuma conta selecionada. Selecione uma conta no menu lateral.");
            return;
        }
        if (!query) return;

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const res = await api.post('/tools/lookup', {
                account_id: accountId,
                query: query
            });
            setResult(res.data);
        } catch (e: any) {
            const errorData = e.response?.data;
            let errorMessage = "Erro ao buscar ID. Verifique o link.";

            if (errorData?.error) {
                errorMessage = errorData.error;
                if (errorData.suggestion) {
                    errorMessage += `\n\n💡 ${errorData.suggestion}`;
                }
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setToast({ msg: "ID Copiado!", type: "success" });
    };

    const renderType = (type: string) => {
        const t = type.toUpperCase();
        if (t.includes('CHANNEL')) return 'Canal';
        if (t.includes('CHAT')) return 'Grupo'; // Basic Chat is Grupo
        if (t.includes('USER')) return 'Usuário';
        if (t.includes('BOT')) return 'Bot';
        return type;
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3><Search size={20} color="#ff6a00" /> Descobridor de ID</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="modal-body">


                    <div className="input-group-pro">
                        <div className="input-wrapper" style={{ padding: '8px 12px' }}>
                            <input
                                placeholder="Link de convite ou @usuario..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                                autoFocus
                            />
                            <button
                                className="btn-search-icon"
                                onClick={handleLookup}
                                disabled={loading || !query}
                            >
                                {loading ? <div className="loader-sm"></div> : <Search size={18} strokeWidth={2.5} />}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="warning-box" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', whiteSpace: 'pre-line' }}>
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}

                    {result && (
                        <div className="result-card">
                            <div className="result-header">
                                <div className="result-avatar">
                                    {result.title ? result.title.substring(0, 1).toUpperCase() : '?'}
                                </div>
                                <div className="result-info">
                                    <div className="result-value">{result.title || "Sem Título"}</div>
                                    <div className="type-badge">{renderType(result.type)}</div>
                                </div>
                            </div>

                            <div className="copy-row">
                                <span className="label-id">ID</span>
                                <code className="id-code">
                                    {result.id}
                                </code>
                                <button className="btn-copy-sm" onClick={() => copyToClipboard(str(result.id))}>
                                    <Copy size={14} />
                                </button>
                            </div>

                            {onSelectId && (
                                <div className="action-grid">
                                    <button className="btn-cancel" onClick={() => onSelectId(str(result.id), 'from', result.title)}>
                                        Usar na Origem
                                    </button>
                                    <button className="btn-create-pro" onClick={() => onSelectId(str(result.id), 'to', result.title)} style={{ fontSize: '12px', padding: '10px' }}>
                                        Usar no Destino <ArrowRight size={14} />
                                    </button>
                                </div>
                            )}

                            {result.note && (
                                <div className="warning-box">
                                    <AlertTriangle size={14} /> {result.note}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function str(id: any): string {
    return id + "";
}
